import { CREATE_FOLDER_MUTATION } from '../gql/mutations/flowcode'
import {
  CreateFolderResponseType,
  CreateFolderVariablesType,
  GetFlowcodeDirectoriesResponseType
} from '../types/flowcode.types'
import { Dispatch } from '@reduxjs/toolkit'
import {
  EventType,
  FetchFlowcodesQueryResponseType,
  FilterDirection,
  FlowcodeFolderType,
  FlowcodeNodeType,
  FlowcodeType,
  GetOrderByType,
  LandingPageDestination,
  LandingPageDestinationType,
  LandingPageFormValuesType,
  ManagementFilter
} from '@dtx-company/inter-app/src/types/flowcode'
import { FetchFolderQueryResponseType } from '../types/folder.types'
import { GetSortDirectionsType } from '../types/code-management.types'
import {
  LANDING_PAGE_DESTINATIONS,
  LANDING_PAGE_FORM_INITIAL_VALUES
} from '../constants/landing-page'
import { PageInfoType } from '../types/api.types'
import { SortDirections } from '@dtx-company/inter-app/src/types/pageManagement'
import { TableCellProps } from '@dtx-company/shared-components/src'
import { format, parseISO } from 'date-fns'
import {
  getEmailParams,
  getPhoneParams,
  getSMSParams,
  getVenmoParams,
  getWiFiParams
} from '@dtx-company/flowcode-utils/src'
import { getVCardProperties } from '../components/landing-page/destination-form/VCardDestinationForm/utils'
import { getWiFiRedirectValue } from '@app/code/src/utils/flowcode'
import { gqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import { setFlowcodesFilter } from '@dtx-company/inter-app/src/redux/slices/code-management-slice'

// TODO - remove this, event form values cannot be updated and are no longer displayed on the details page - DF
export const getEventFormValues = (_redirectValue: string): EventType => {
  return {
    name: '',
    location: '',
    url: '',
    startTime: '',
    endTime: ''
  }
}

/**
 * Used on the Flowcode Detail page to deconstruct the redirect value and populate the destination form fields.
 * @param redirectValue
 * @param type
 * @param flowcode
 */
export const getEditFlowcodeInitialValues = async (
  redirectValue: string,
  type: LandingPageDestination,
  flowcode: FlowcodeType,
  token?: string
): Promise<LandingPageFormValuesType> => {
  const values = { ...LANDING_PAGE_FORM_INITIAL_VALUES }

  switch (type) {
    case LandingPageDestination.FILE:
      // creating file object with size 0 to pass to the destination form
      values.file = new File([], flowcode?.assetFileName || '')
      break
    case LandingPageDestination.PHONE:
      values.phone = getPhoneParams(redirectValue).phoneNumber
      break
    case LandingPageDestination.SMS:
      const smsParams = getSMSParams(redirectValue)
      values.sms = {
        ...smsParams,
        message: smsParams.message || ''
      }
      break
    case LandingPageDestination.EMAIL:
      const emailParams = getEmailParams(redirectValue)
      values.email = {
        ...emailParams,
        subject: emailParams.subject || '',
        body: emailParams.body || ''
      }
      break
    case LandingPageDestination.VENMO:
      const venmoParams = getVenmoParams(redirectValue)
      values.venmo = {
        ...getVenmoParams(redirectValue),
        amount: venmoParams.amount || undefined,
        note: venmoParams.note || ''
      }
      break
    case LandingPageDestination.EVENT:
      values.event = getEventFormValues(redirectValue)
      break
    case LandingPageDestination.WIFI:
      values.wifi = getWiFiParams(redirectValue)
      break
    case LandingPageDestination.VCARD:
      values.vcard = await getVCardProperties(redirectValue, token)
      break
    default:
      values.link = redirectValue
  }

  return values
}

/**
 *
 * Returns a formatted label to display instead of the actual redirect value
 */
export const getFormattedRedirectValue = (value: string, type?: LandingPageDestination): string => {
  if (!value || !type) return ''
  if (type === LandingPageDestination.SMS) return `SMS ${getSMSParams(value).phoneNumber}`
  if (type === LandingPageDestination.EVENT) return 'Event'
  if (type === LandingPageDestination.EMAIL) return getEmailParams(value).email
  if (type === LandingPageDestination.VENMO) return `@${getVenmoParams(value).recipients}`
  if (type === LandingPageDestination.WIFI) {
    const data = getWiFiRedirectValue(value)
    return `WIFI network: ${data?.name}`
  }

  return value
}

export const createFolder = (
  variables: CreateFolderVariablesType
): Promise<CreateFolderResponseType> => {
  return gqlFetcher(CREATE_FOLDER_MUTATION, variables)
}

export const getFlowcodeEdges = (
  data: FetchFlowcodesQueryResponseType | FetchFolderQueryResponseType,
  isFolder = false
): FlowcodeNodeType[] => {
  return isFolder
    ? (data as FetchFolderQueryResponseType).myFlowcodes?.allBatches?.edges
    : (data as FetchFlowcodesQueryResponseType).myFlowcodes?.allBatches?.edges
}

// TOOD - research if react query cna preform this merge - DF
export const reduceFlowcodes = (
  data: (FetchFlowcodesQueryResponseType | FetchFolderQueryResponseType)[] | undefined,
  isFolder = false
): FlowcodeNodeType[] | undefined => {
  if (!data) return
  return data?.reduce((prev, curr) => {
    const edges = getFlowcodeEdges(curr, isFolder)
    return [...prev, ...edges]
  }, [] as unknown as FlowcodeNodeType[])
}

// SWR does not merge the responses. This util is used to manually merge the responses.
export const parseMyFlowcodesResponse = (
  data?: FetchFlowcodesQueryResponseType[]
): {
  flowcodes: FlowcodeNodeType[] | undefined
  pageInfo: PageInfoType | undefined
  highlightedFlowcodes: FlowcodeNodeType[] | undefined
} => {
  const lastElement = data && data[data.length - 1]
  // the pagination for this query is ignored, use the most recent info
  const highlightedFlowcodes = lastElement?.myFlowcodes?.highlighted?.edges
  const pageInfo = lastElement?.myFlowcodes?.allBatches.pageInfo
  const flowcodes = reduceFlowcodes(data)
  return {
    flowcodes,
    pageInfo,
    highlightedFlowcodes
  }
}

export const parseMyFoldersResponse = (
  data?: GetFlowcodeDirectoriesResponseType
): { folders: FlowcodeFolderType['node'][] | undefined } => {
  const folders = data?.directories.allDirs.edges.map(edge => {
    return edge.node
  })
  return {
    folders
  }
}

export const getOrderBy = (
  name: ManagementFilter,
  direction: FilterDirection,
  highlightName: ManagementFilter,
  highlightDirection: FilterDirection
): GetOrderByType => {
  let foldersManagementFilter, folderDirection
  if (['nickname', 'time_stamp'].includes(name)) {
    foldersManagementFilter = name === 'nickname' ? 'name' : name
    folderDirection = direction
  } else {
    foldersManagementFilter = 'name'
    folderDirection = FilterDirection.DSC
  }
  return {
    foldersOrderBy: `${folderDirection}${foldersManagementFilter}`,
    flowcodesOrderBy: `${direction}${name}`,
    highlightedOrderBy: `${highlightDirection}${highlightName}`
  }
}

export const handleSortNameBtnClick: (dispatch: Dispatch) => TableCellProps['onClick'] =
  dispatch => (_, sortDirection) => {
    dispatch(
      setFlowcodesFilter({
        direction: sortDirection || FilterDirection.ASC,
        name: ManagementFilter.NAME
      })
    )
  }

export const handleSortByTimestampBtnClick: (dispatch: Dispatch) => TableCellProps['onClick'] =
  dispatch => (_, sortDirection) => {
    dispatch(
      setFlowcodesFilter({
        direction: sortDirection || FilterDirection.ASC,
        name: ManagementFilter.TIMESTAMP
      })
    )
  }

export const handleSortByTotalScansBtnClick: (dispatch: Dispatch) => TableCellProps['onClick'] =
  dispatch => (_, sortDirection) => {
    dispatch(
      setFlowcodesFilter({
        direction: sortDirection || FilterDirection.ASC,
        name: ManagementFilter.SCANCOUNT
      })
    )
  }

export const handleSortByLastScanDateBtnClick: (dispatch: Dispatch) => TableCellProps['onClick'] =
  dispatch => (_, sortDirection) => {
    dispatch(
      setFlowcodesFilter({
        direction: sortDirection || FilterDirection.ASC,
        name: ManagementFilter.SCANTIME
      })
    )
  }

export const handleSortByPermissionsBtnClick: (dispatch: Dispatch) => TableCellProps['onClick'] =
  dispatch => (_, sortDirection) => {
    dispatch(
      setFlowcodesFilter({
        direction: sortDirection || FilterDirection.ASC,
        name: ManagementFilter.PERMISSIONS
      })
    )
  }

export const getSortDirections = (
  selectedFilter: ManagementFilter,
  direction: FilterDirection,
  currentfilter: ManagementFilter
): GetSortDirectionsType => {
  if (currentfilter === selectedFilter) {
    const sortDirection = direction == FilterDirection.ASC ? SortDirections.ASC : SortDirections.DSC
    return {
      type: 'sort',
      initialSortDirection: sortDirection,
      controlledSortDirection: sortDirection
    }
  }
  return {}
}

/**
 *
 * Subtracts the timezone offset from incomplete dates that are converted to ISO
 *
 */
export const formatDateWithoutTimezone = (timestamp: string): string => {
  return format(parseISO(timestamp), 'MMM dd, yyyy')
}

export const formatDate = (timestamp: string | Date): string => {
  return format(timestamp ? new Date(timestamp) : Date.now(), 'MMM dd, yyyy')
}

export const formatDateMMDDYYYY = (timestamp: string | Date): string => {
  return format(timestamp ? new Date(timestamp) : Date.now(), 'MM/dd/yy')
}

export const getSelectedDestination = (
  originalRedirectType: LandingPageDestination
): LandingPageDestinationType =>
  LANDING_PAGE_DESTINATIONS.find(
    landingPageDestination => landingPageDestination.id === originalRedirectType
  ) || LANDING_PAGE_DESTINATIONS[0]
